/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

// libraries
import React, { useEffect } from 'react';
import {
    object, string, array,
    bool,
    func,
} from 'prop-types';
import { useQuery } from '@apollo/client';
import mbpLogger from 'mbp-logger';

// redux, hooks, helpers
import { useDispatch, useSelector } from 'react-redux';
import findContent from '../../../../../gql/queries/findContent';
import { GRAPHQL_ENV } from '../../../../../gql';
import PLAPDPIcon from './PLAPDPIcon';
import { trackEvent } from '../../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import { getFeatureFlag } from '../../../../../../state/ducks/App/ducks/Config/Config-Selectors';
import useIsPlaPage from '../../../../../helpers/hooks/useIsPlaPage';
import useIsDisplayChannel from '../../../../../helpers/hooks/useIsDisplayChannel';
import PlaRelatedCollection from './PLARelatedCollection';
import noop from '../../../../../helpers/noop';

const PLAPDPIconQuery = ({
    brand, partNumber, productName, plaRedesignDesktop, trackClickThrough,
}) => {
    const isPLAPDPIconEnabled = useSelector(getFeatureFlag('is-pla-pdp-icons-enabled'));
    const isDisplayChannel = useIsDisplayChannel();
    const isPlaEnabled = useIsPlaPage() || isDisplayChannel;
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(trackEvent({
            eventCategory: 'PLA PDP Icon',
            eventAction: 'Test Impression',
            eventLabel: `Variant - ${partNumber} - ${productName?.[0]}`,
            nonInteraction: '1',
        }));
    }, []);

    const findPLAIcon = {
        brand: brand['domain-name'],
        contentType: 'nup_v3',
        query: '\\"title\\": \\"PLA PDP\\"',
        GRAPHQL_ENV,
    };
    const PLA_ICON_QUERY = findContent(findPLAIcon);
    const { loading, error, data } = useQuery(PLA_ICON_QUERY, {
        skip: !isPlaEnabled || !isPLAPDPIconEnabled,
    });
    if (loading) {
        return null;
    }
    if (error) {
        mbpLogger.logError({
            appName: process.env.npm_package_name,
            query: PLA_ICON_QUERY,
            jsError: error,
            component: 'PLAPDPIconQuery.js',
            message: `Failed to fetch content data for: content type nup_v3 on brand ${brand}`,
            env: GRAPHQL_ENV,
        });
    }
    if (!data || !data.findContent?.content || !data.findContent.content?.entries
        || !Array.isArray(data.findContent.content.entries)) {
        mbpLogger.logWarning({
            appName: process.env.npm_package_name,
            query: PLA_ICON_QUERY,
            component: 'PLAPDPIconQuery.js',
            message: 'No data returned for query',
            env: GRAPHQL_ENV,
        });
        return null;
    }

    if (!isPlaEnabled || !isPLAPDPIconEnabled) { return null; }
    if (plaRedesignDesktop) {
        return (
            <>
                <PlaRelatedCollection
                    data={data.findContent.content.entries[0]?.entries}
                    trackClickThrough={trackClickThrough}
                    isPersonalizedProp
                />
            </>
        );
    }
    return (
        <PLAPDPIcon
            blockData={data.findContent.content.entries[0]}
            trackClickThrough={trackClickThrough}
            isPersonalizedProp
        />
    );
};

PLAPDPIconQuery.propTypes = {
    brand: object,
    partNumber: string.isRequired,
    productName: array.isRequired,
    plaRedesignDesktop: bool,
    trackClickThrough: func,
};

PLAPDPIconQuery.defaultProps = {
    brand: {},
    plaRedesignDesktop: false,
    trackClickThrough: noop,
};

export default PLAPDPIconQuery;
